import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useSession } from "next-auth/react";
import {
  readSessionCache,
  removeSessionCache,
  writeSessionCache,
} from "../utils/sessionCache";

export type SavedArticle = {
  id: string;
  title: string;
  thumbnail?: string;
  description?: string;
  publishDt?: string;
  category?: string;
  savedAt: string;
  author?: { _id?: string; displayName?: string; pictureProfile?: string }[];
  game?: { _id?: string; title?: string }[];
};

type SavedArticlesContextType = {
  savedArticles: SavedArticle[];
  isSaved: (id: string) => boolean;
  toggleSave: (article: Omit<SavedArticle, "savedAt">) => void;
  removeSave: (id: string) => void;
};

const SavedArticlesContext = createContext<SavedArticlesContextType>({
  savedArticles: [],
  isSaved: () => false,
  toggleSave: () => {},
  removeSave: () => {},
});

// A fresh per-tab copy of the list skips the mount fetch. The user's own
// saves/removes are written through below, so only another tab's changes can
// lag, and at most by this much.
const SAVED_CACHE_TTL = 5 * 60_000;
const cacheKeyFor = (userId?: string) =>
  userId ? `gow:saved-articles:${userId}` : null;

export const SavedArticlesProvider = ({ children }: { children: React.ReactNode }) => {
  const { data: session } = useSession();
  const [savedArticles, setSavedArticles] = useState<SavedArticle[]>([]);
  const token = session?.user?.accessToken;
  const cacheKey = cacheKeyFor(session?.user?._id);
  const loadedRef = useRef(false);
  const seededValueRef = useRef<SavedArticle[] | null>(null);
  const lastCacheKeyRef = useRef<string | null>(null);

  // Write-through for saves/removes and the network refresh. Declared before
  // the loader so it never sees the pre-seed [] as loaded, and it skips the
  // seeded array itself so a reload does not re-stamp a stale entry as fresh.
  useEffect(() => {
    if (!token || !cacheKey || !loadedRef.current) return;
    if (savedArticles === seededValueRef.current) return;
    writeSessionCache(cacheKey, savedArticles);
  }, [savedArticles, token, cacheKey]);

  useEffect(() => {
    if (!token) {
      setSavedArticles([]);
      loadedRef.current = false;
      seededValueRef.current = null;
      if (lastCacheKeyRef.current) {
        removeSessionCache(lastCacheKeyRef.current);
        lastCacheKeyRef.current = null;
      }
      return;
    }
    lastCacheKeyRef.current = cacheKey;

    const cached = cacheKey ? readSessionCache<SavedArticle[]>(cacheKey) : undefined;
    if (cached && Array.isArray(cached.value)) {
      seededValueRef.current = cached.value;
      setSavedArticles(cached.value);
      loadedRef.current = true;
      if (Date.now() - cached.at < SAVED_CACHE_TTL) return;
    }

    fetch(`${process.env.BACKEND_BASE_URL}/contents/user/saved`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && Array.isArray(data.data)) {
          const articles: SavedArticle[] = data.data.map((content: any) => ({
            id: content._id,
            title: content.title,
            thumbnail: content.thumbnail,
            description: content.description,
            publishDt: content.publishDt,
            category: content.category,
            savedAt: content.savedAt || new Date().toISOString(),
            author: Array.isArray(content.author)
              ? content.author
              : content.author
              ? [content.author]
              : [],
            game: content.game,
          }));
          loadedRef.current = true;
          setSavedArticles(articles);
        }
      })
      .catch(() => {});
  }, [token, cacheKey]);

  const isSaved = (id: string) => savedArticles.some((a) => a.id === id);

  const toggleSave = (article: Omit<SavedArticle, "savedAt">) => {
    if (!session?.user?.accessToken) return;

    if (isSaved(article.id)) {
      fetch(`${process.env.BACKEND_BASE_URL}/contents/saved/${article.id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.user.accessToken}`,
        },
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            setSavedArticles((prev) => prev.filter((a) => a.id !== article.id));
          }
        })
        .catch(() => {});
    } else {
      fetch(`${process.env.BACKEND_BASE_URL}/contents/saved/${article.id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.user.accessToken}`,
        },
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            setSavedArticles((prev) => [
              { ...article, savedAt: new Date().toISOString() },
              ...prev,
            ]);
          }
        })
        .catch(() => {});
    }
  };

  const removeSave = (id: string) => {
    if (!session?.user?.accessToken) return;

    fetch(`${process.env.BACKEND_BASE_URL}/contents/saved/${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.user.accessToken}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setSavedArticles((prev) => prev.filter((a) => a.id !== id));
        }
      })
      .catch(() => {});
  };

  return (
    <SavedArticlesContext.Provider value={{ savedArticles, isSaved, toggleSave, removeSave }}>
      {children}
    </SavedArticlesContext.Provider>
  );
};

export const useSavedArticles = () => useContext(SavedArticlesContext);
